import {
  APPROVED_FORMULA_BLOCKED_REASON_CODES,
  type ApprovedFormulaBlockedReasonCode,
} from './approved-formula-reason-codes';
import {
  ASK_HELIX_ANSWER_VERDICT_STATUSES,
  type AskHelixAnswerVerdictStatusDto,
} from './ask-helix-answer-verdict-statuses';
import {
  BRIEF_BLOCK_REASON_CODES,
  type BriefBlockReasonCode,
} from './brief-block-reason-codes';
import {
  ORG_SCORECARD_BLOCKED_REASON_CODES_V1,
  type OrgScorecardBlockedReasonCodeV1,
} from './org-scorecard-reason-codes';
import {
  INFERENCE_BLOCKED_REASON_CODES,
  type InferenceBlockedReasonCode,
} from './symbolic-inference-reason-codes';
import {
  OBJECTIVE_PACKAGE_LEVER_INCLUSION_STATES,
  type ObjectivePackageLeverInclusionState,
} from './ask-helix/contracts/objective-package-solver';
import {
  GATE_LEDGER_SILENT_REASON_CODES,
  type GateLedgerSilentReasonCode,
} from './gate-ledger-silent-reason-codes';
export {
  GATE_LEDGER_SILENT_REASON_CODES,
  type GateLedgerSilentReasonCode,
} from './gate-ledger-silent-reason-codes';
// #7322 inc.2 HOLD fix (size law): moved to gate-ledger-egress-reason-codes.ts, re-exported.
import { GATE_LEDGER_EGRESS_REASON_CODES, type GateLedgerEgressReasonCode } from './gate-ledger-egress-reason-codes';
export { GATE_LEDGER_EGRESS_REASON_CODES, type GateLedgerEgressReasonCode };

export const GATE_LEDGER_SCHEMA_VERSION = 'gate_ledger_v1' as const;
export const GATE_LEDGER_SNAPSHOT_SCHEMA_VERSION =
  'gate_ledger_snapshot_v1' as const;

export const ASK_HELIX_GATE_IDS = Object.freeze([
  'route.admission',
  'route.candidates',
  'route.planning_hints',
  'capability.admission',
  'evidence.posture',
  'evidence.freshness',
  'claim.overlap_keys',
  'scenario.policy',
  'db.formula_activation',
  'db.formula_digest',
  'db.formula_binding',
  'db.source_system_activation',
  'db.artifact_admission',
  'db.ingest_lineage',
  'db.metric_registry',
  'db.inference_rule',
  'engine.aggregate_emission',
  // #4008 — the value-gap source-intent selector refused to pick an owner for a
  // routable lever, so the lever was skipped. Recorded so the drop is queryable.
  'routing.lever_source_intent',
  // #5528 -- the objective package's per-lever candidacy receipt (obligation-
  // driven inclusion/withhold/skip). Recorded at each solved lever, not just at
  // the package boundary, so a single withheld lever is queryable on its own.
  'package.candidacy',
  'compose.component_builder',
  'compose.flagship_lever_sizing',
  'compose.deck_lever_drop',
  'grade.proof_gate',
  'grade.answer_grader',
  'spec.view_catalog',
  'spec.component_props',
  'spec.proof_refs',
  'egress.binding_fence',
  'egress.sanitizer',
  'guard.module_identity_write',
  'web.fold_value_ladder',
  'web.shell_runtime_state',
] as const);

export type AskHelixGateId = (typeof ASK_HELIX_GATE_IDS)[number];

export const GATE_LEDGER_LAYERS = Object.freeze([
  'routing',
  'admission',
  'db_state',
  'engine_emit',
  'compose',
  'grade',
  'egress',
  'web_render',
] as const);

export type GateLedgerLayer = (typeof GATE_LEDGER_LAYERS)[number];

export const GATE_LEDGER_OUTCOMES = Object.freeze([
  'pass',
  'block',
  'withhold',
  'degrade',
  'redact',
  'skip',
  'not_consulted',
] as const);

export type GateLedgerOutcome = (typeof GATE_LEDGER_OUTCOMES)[number];

export const GATE_LEDGER_SUBJECT_KINDS = Object.freeze([
  'answer',
  'component',
  'metric',
  'claim',
  'action',
  'route',
  'write',
] as const);

export type GateLedgerSubjectKind = (typeof GATE_LEDGER_SUBJECT_KINDS)[number];

export const GATE_LEDGER_VALUE_STATES = Object.freeze([
  'present',
  'absent',
  'redacted',
  'null',
] as const);

export type GateLedgerValueState = (typeof GATE_LEDGER_VALUE_STATES)[number];

export const ASK_HELIX_INTERNAL_BLOCKED_REASON_CODES = Object.freeze([
  'surface_gap',
  'subject_gap',
  'opening_demand',
  'objective_health_semantics_gap',
  'objective_health_source_unavailable',
  'objective_resource_actuals_unavailable',
  'objective_intervention_options_unavailable',
  'historical_window_unavailable',
  'cross_domain_semantics_gap',
  'tenant_scope_violation',
  'provider_actuals_unavailable',
  'approval_evidence_required',
  'finance_artifact_required',
  'brief_evidence_required',
  'individual_adverse_action',
  'explicit_missing_truth_number',
  'decision_agent_truth_gap',
  'briefs_agent_truth_gap',
  'strategy_workspace_truth_gap',
  'portfolio_plan_required',
  'answer_shape_gap',
  'provider_unavailable',
  'synthesis_unavailable',
  'narration_unavailable',
  'governed_narration_numeric_binding_missing',
  'governed_narration_withhold_posture_invalid',
  'governed_narration_unsupported_frame_claim',
  'governed_narration_action_authority_violation',
] as const);

export type AskHelixInternalBlockedReasonCode =
  (typeof ASK_HELIX_INTERNAL_BLOCKED_REASON_CODES)[number];

export const CAPABILITY_ADMISSION_REASON_CODES = Object.freeze([
  'ambiguous_entity',
  'capability_not_in_context_catalog',
  'capability_tool_mismatch',
  'capability_unavailable',
  'invalid_input',
  'missing_evidence',
  'missing_scenario_input',
  'permission_denied',
  'provider_plan_required',
  'scenario_opt_in_required',
  'stale_context',
  'stale_evidence',
  'unregistered_capability',
  'unsupported_capability_intent',
  // #7322 inc.2 — requested grain/period not in the metric's declared set.
  'unsupported_metric_grain',
] as const);

export type CapabilityAdmissionReasonCode =
  (typeof CAPABILITY_ADMISSION_REASON_CODES)[number];

export const GOVERNED_METRIC_REGISTRY_REASON_CODES = Object.freeze([
  'metric_registry_missing',
  'metric_registry_inactive',
  'metric_registry_ambiguous',
  'metric_registry_missing_compiled_definition',
  'metric_definition_digest_drift',
  'metric_registry_scope_mismatch',
  'metric_registry_scope_invalid',
  'dimension_catalog_missing_compiled_definition',
  'dimension_definition_digest_drift',
] as const);

export type GovernedMetricRegistryReasonCode =
  (typeof GOVERNED_METRIC_REGISTRY_REASON_CODES)[number];

export const DRIFT_EXCEPTION_BLOCKED_REASON_CODES = Object.freeze([
  'decision_ledger_delegate_unavailable',
] as const);

export const GATE_LEDGER_REASON_CODE_CATALOGS = Object.freeze({
  inference: INFERENCE_BLOCKED_REASON_CODES,
  brief: BRIEF_BLOCK_REASON_CODES,
  approvedFormula: APPROVED_FORMULA_BLOCKED_REASON_CODES,
  orgScorecard: ORG_SCORECARD_BLOCKED_REASON_CODES_V1,
  internalPipeline: ASK_HELIX_INTERNAL_BLOCKED_REASON_CODES,
  metricRegistry: GOVERNED_METRIC_REGISTRY_REASON_CODES,
  driftException: DRIFT_EXCEPTION_BLOCKED_REASON_CODES,
  answerVerdict: ASK_HELIX_ANSWER_VERDICT_STATUSES,
});

export const GATE_LEDGER_VIEW_SPEC_REASON_CODES = Object.freeze([
  'unknown_view_type',
  'invalid_schema_version',
  'unknown_component_type',
  'missing_required_prop',
  'invalid_prop_type',
  'missing_permission',
  'non_displayable_proof_ref',
  'non_activatable_proof_ref',
  'stale_proof_ref',
  'missing_proof_ref',
] as const);

export type GateLedgerViewSpecReasonCode =
  (typeof GATE_LEDGER_VIEW_SPEC_REASON_CODES)[number];

export const GATE_LEDGER_PACKAGE_CANDIDACY_REASON_CODES = Object.freeze(
  OBJECTIVE_PACKAGE_LEVER_INCLUSION_STATES.filter(
    (state): state is Exclude<ObjectivePackageLeverInclusionState, 'included'> =>
      state !== 'included',
  ),
);

export type GateLedgerPackageCandidacyReasonCode =
  (typeof GATE_LEDGER_PACKAGE_CANDIDACY_REASON_CODES)[number];

export type GateLedgerReasonCode =
  | InferenceBlockedReasonCode
  | BriefBlockReasonCode
  | ApprovedFormulaBlockedReasonCode
  | OrgScorecardBlockedReasonCodeV1
  | AskHelixInternalBlockedReasonCode
  | CapabilityAdmissionReasonCode
  | GovernedMetricRegistryReasonCode
  | 'decision_ledger_delegate_unavailable'
  | AskHelixAnswerVerdictStatusDto
  | GateLedgerViewSpecReasonCode
  | GateLedgerEgressReasonCode
  | GateLedgerPackageCandidacyReasonCode
  | 'horizon_context_kind_required'
  | GateLedgerSilentReasonCode;

export const GATE_LEDGER_REASON_CODES: readonly GateLedgerReasonCode[] =
  Object.freeze(
    Array.from(
      new Set<GateLedgerReasonCode>([
        ...INFERENCE_BLOCKED_REASON_CODES,
        ...BRIEF_BLOCK_REASON_CODES,
        ...APPROVED_FORMULA_BLOCKED_REASON_CODES,
        ...ORG_SCORECARD_BLOCKED_REASON_CODES_V1,
        ...ASK_HELIX_INTERNAL_BLOCKED_REASON_CODES,
        ...CAPABILITY_ADMISSION_REASON_CODES,
        ...GOVERNED_METRIC_REGISTRY_REASON_CODES,
        ...DRIFT_EXCEPTION_BLOCKED_REASON_CODES,
        ...ASK_HELIX_ANSWER_VERDICT_STATUSES,
        ...GATE_LEDGER_VIEW_SPEC_REASON_CODES,
        ...GATE_LEDGER_EGRESS_REASON_CODES,
        ...GATE_LEDGER_PACKAGE_CANDIDACY_REASON_CODES,
        'horizon_context_kind_required',
        ...GATE_LEDGER_SILENT_REASON_CODES,
      ]),
    ),
  );

export const GATE_LEDGER_MAX_SERIALIZED_BYTES = 131_072;
export const GATE_LEDGER_MAX_ENTRIES = 500;
export const GATE_LEDGER_MAX_DB_STATES = 200;
export const GATE_LEDGER_MAX_ARRAY_ITEMS = 100;
export const GATE_LEDGER_MAX_MAP_FIELDS = 32;
export const GATE_LEDGER_MAX_STRING_LENGTH = 512;

export type GateLedgerEntryV1 = Readonly<{
  seq: number;
  gateId: AskHelixGateId;
  layer: GateLedgerLayer;
  outcome: GateLedgerOutcome;
  reasonCode: GateLedgerReasonCode | null;
  subjectKind: GateLedgerSubjectKind;
  subjectKey: string;
  valueState: GateLedgerValueState;
  inputDigests: Readonly<Record<string, string>>;
  evidenceRefs: readonly string[];
  detailRef: string | null;
  elapsedMs: number | null;
  source: 'server' | 'client';
}>;

export type GateLedgerDbStateV1 = Readonly<{
  table: string;
  key: string;
  status: string;
  decisive: boolean;
  stateFields?: Readonly<Record<string, string | null>>;
}>;

export type GateLedgerRedactionV1 = Readonly<{
  kind: 'person_identifier' | 'bare_numeric';
  count: number;
}>;

export type GateLedgerRoutingV1 = Readonly<{
  intentClass: string | null;
  chosenTools: readonly string[];
  forcedHints: readonly string[];
  nearExactScore: number | null;
  /**
   * P0c (#2912) — per-turn routing receipt (ADDITIVE observability only; not
   * a fence). Compact provenance strings:
   * - candidateSummary: one entry per offered route candidate,
   *   `capabilityId:intentId:score:matchKind:channel` (channel 'lexical'
   *   today; 'embedding' joins when the P2 channel lands).
   * - proposalSummary: the planner proposal as disposed — proposed tool
   *   names plus the proposal intentClass.
   * - disposalOutcome: `pass` or `block:<reasonCode>` from route admission.
   */
  candidateSummary?: readonly string[];
  proposalSummary?: readonly string[];
  disposalOutcome?: string | null;
}>;

export type GateLedgerVerdictV1 = Readonly<{
  status: AskHelixAnswerVerdictStatusDto;
  primaryReasonCode: GateLedgerReasonCode | null;
  primaryGateId: AskHelixGateId | null;
  recoverable: boolean;
}>;

export type GateLedgerV1 = Readonly<{
  schemaVersion: typeof GATE_LEDGER_SCHEMA_VERSION;
  turnId: string;
  tenantId: string;
  questionDigest: `sha256:${string}`;
  askedAt: string;
  verdict: GateLedgerVerdictV1;
  entries: readonly GateLedgerEntryV1[];
  dbStatesConsulted: readonly GateLedgerDbStateV1[];
  redactions: readonly GateLedgerRedactionV1[];
  routing: GateLedgerRoutingV1;
}>;

export type GateLedgerSnapshotEnvelopeV1 = Readonly<{
  schemaVersion: typeof GATE_LEDGER_SNAPSHOT_SCHEMA_VERSION;
  runId: string;
  requestId: string;
  tenantId: string;
  turnId: string;
  ledgerDigest: `sha256:${string}`;
  gateLedger: GateLedgerV1;
}>;

export function parseGateLedgerV1(
  value: unknown,
  expected: Readonly<{ tenantId?: string; turnId?: string }> = {},
): GateLedgerV1 | null {
  if (!isRecord(value) || !hasOnlyKeys(value, GATE_LEDGER_KEYS)) return null;
  const serialized = safeJson(value);
  if (
    serialized === null ||
    utf8ByteLength(serialized) > GATE_LEDGER_MAX_SERIALIZED_BYTES
  ) {
    return null;
  }
  if (
    value.schemaVersion !== GATE_LEDGER_SCHEMA_VERSION ||
    !isBoundedString(value.turnId) ||
    !isBoundedString(value.tenantId) ||
    (expected.turnId !== undefined && value.turnId !== expected.turnId) ||
    (expected.tenantId !== undefined && value.tenantId !== expected.tenantId) ||
    !isSha256Digest(value.questionDigest) ||
    !isBoundedString(value.askedAt) ||
    !Number.isFinite(Date.parse(value.askedAt)) ||
    !parseGateLedgerVerdict(value.verdict) ||
    !parseGateLedgerEntries(value.entries) ||
    !parseGateLedgerDbStates(value.dbStatesConsulted) ||
    !parseGateLedgerRedactions(value.redactions) ||
    !parseGateLedgerRouting(value.routing)
  ) {
    return null;
  }
  return value as GateLedgerV1;
}

export function parseGateLedgerSnapshotEnvelopeV1(
  value: unknown,
  expected: Readonly<{
    runId?: string;
    requestId?: string;
    tenantId?: string;
    turnId?: string;
  }> = {},
): GateLedgerSnapshotEnvelopeV1 | null {
  if (!isRecord(value) || !hasOnlyKeys(value, GATE_LEDGER_SNAPSHOT_KEYS)) {
    return null;
  }
  if (
    value.schemaVersion !== GATE_LEDGER_SNAPSHOT_SCHEMA_VERSION ||
    !isBoundedString(value.runId) ||
    !isBoundedString(value.requestId) ||
    !isBoundedString(value.tenantId) ||
    !isBoundedString(value.turnId) ||
    !isSha256Digest(value.ledgerDigest) ||
    (expected.runId !== undefined && value.runId !== expected.runId) ||
    (expected.requestId !== undefined && value.requestId !== expected.requestId) ||
    (expected.tenantId !== undefined && value.tenantId !== expected.tenantId) ||
    (expected.turnId !== undefined && value.turnId !== expected.turnId)
  ) {
    return null;
  }
  const gateLedger = parseGateLedgerV1(value.gateLedger, {
    tenantId: value.tenantId,
    turnId: value.turnId,
  });
  return gateLedger ? (value as GateLedgerSnapshotEnvelopeV1) : null;
}

const GATE_LEDGER_KEYS = Object.freeze([
  'schemaVersion',
  'turnId',
  'tenantId',
  'questionDigest',
  'askedAt',
  'verdict',
  'entries',
  'dbStatesConsulted',
  'redactions',
  'routing',
] as const);

const GATE_LEDGER_SNAPSHOT_KEYS = Object.freeze([
  'schemaVersion',
  'runId',
  'requestId',
  'tenantId',
  'turnId',
  'ledgerDigest',
  'gateLedger',
] as const);

const GATE_LEDGER_ENTRY_KEYS = Object.freeze([
  'seq',
  'gateId',
  'layer',
  'outcome',
  'reasonCode',
  'subjectKind',
  'subjectKey',
  'valueState',
  'inputDigests',
  'evidenceRefs',
  'detailRef',
  'elapsedMs',
  'source',
] as const);

const gateIdSet = new Set<unknown>(ASK_HELIX_GATE_IDS);
const layerSet = new Set<unknown>(GATE_LEDGER_LAYERS);
const outcomeSet = new Set<unknown>(GATE_LEDGER_OUTCOMES);
const subjectKindSet = new Set<unknown>(GATE_LEDGER_SUBJECT_KINDS);
const valueStateSet = new Set<unknown>(GATE_LEDGER_VALUE_STATES);
const verdictStatusSet = new Set<unknown>(ASK_HELIX_ANSWER_VERDICT_STATUSES);
const reasonCodeSet = new Set<unknown>(GATE_LEDGER_REASON_CODES);

function parseGateLedgerVerdict(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, [
      'status',
      'primaryReasonCode',
      'primaryGateId',
      'recoverable',
    ]) &&
    verdictStatusSet.has(value.status) &&
    (value.primaryReasonCode === null || reasonCodeSet.has(value.primaryReasonCode)) &&
    (value.primaryGateId === null || gateIdSet.has(value.primaryGateId)) &&
    typeof value.recoverable === 'boolean'
  );
}

function parseGateLedgerEntries(value: unknown): boolean {
  if (!Array.isArray(value) || value.length > GATE_LEDGER_MAX_ENTRIES) return false;
  return value.every((entry, index) => {
    if (!isRecord(entry) || !hasOnlyKeys(entry, GATE_LEDGER_ENTRY_KEYS)) {
      return false;
    }
    return (
      entry.seq === index + 1 &&
      gateIdSet.has(entry.gateId) &&
      layerSet.has(entry.layer) &&
      outcomeSet.has(entry.outcome) &&
      (entry.reasonCode === null || reasonCodeSet.has(entry.reasonCode)) &&
      subjectKindSet.has(entry.subjectKind) &&
      isBoundedString(entry.subjectKey) &&
      valueStateSet.has(entry.valueState) &&
      isBoundedStringMap(entry.inputDigests) &&
      isBoundedStringArray(entry.evidenceRefs) &&
      (entry.detailRef === null || isBoundedString(entry.detailRef)) &&
      (entry.elapsedMs === null ||
        (typeof entry.elapsedMs === 'number' &&
          Number.isFinite(entry.elapsedMs) &&
          entry.elapsedMs >= 0 &&
          entry.elapsedMs <= 86_400_000)) &&
      (entry.source === 'server' || entry.source === 'client')
    );
  });
}

function parseGateLedgerDbStates(value: unknown): boolean {
  if (!Array.isArray(value) || value.length > GATE_LEDGER_MAX_DB_STATES) return false;
  return value.every(
    (state) =>
      isRecord(state) &&
      hasOnlyKeys(state, ['table', 'key', 'status', 'decisive', 'stateFields']) &&
      isBoundedString(state.table) &&
      isBoundedString(state.key) &&
      isBoundedString(state.status) &&
      typeof state.decisive === 'boolean' &&
      (state.stateFields === undefined || isBoundedNullableStringMap(state.stateFields)),
  );
}

function parseGateLedgerRedactions(value: unknown): boolean {
  if (!Array.isArray(value) || value.length > 2) return false;
  const kinds = new Set<unknown>();
  return value.every((redaction) => {
    if (
      !isRecord(redaction) ||
      !hasOnlyKeys(redaction, ['kind', 'count']) ||
      (redaction.kind !== 'person_identifier' && redaction.kind !== 'bare_numeric') ||
      !Number.isSafeInteger(redaction.count) ||
      (redaction.count as number) <= 0 ||
      (redaction.count as number) > 1_000_000 ||
      kinds.has(redaction.kind)
    ) {
      return false;
    }
    kinds.add(redaction.kind);
    return true;
  });
}

function parseGateLedgerRouting(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, [
      'intentClass',
      'chosenTools',
      'forcedHints',
      'nearExactScore',
      // P0c (#2912) — routing receipt fields (additive observability only).
      'candidateSummary',
      'proposalSummary',
      'disposalOutcome',
    ]) &&
    (value.intentClass === null || isBoundedString(value.intentClass)) &&
    isBoundedStringArray(value.chosenTools) &&
    isBoundedStringArray(value.forcedHints) &&
    (value.nearExactScore === null ||
      (typeof value.nearExactScore === 'number' &&
        Number.isFinite(value.nearExactScore) &&
        value.nearExactScore >= 0 &&
        value.nearExactScore <= 1)) &&
    (value.candidateSummary === undefined ||
      isBoundedStringArray(value.candidateSummary)) &&
    (value.proposalSummary === undefined ||
      isBoundedStringArray(value.proposalSummary)) &&
    (value.disposalOutcome === undefined ||
      value.disposalOutcome === null ||
      isBoundedString(value.disposalOutcome))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean {
  const allowed = new Set(allowedKeys);
  return Object.keys(value).every((key) => allowed.has(key));
}

function isBoundedString(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= GATE_LEDGER_MAX_STRING_LENGTH
  );
}

function isSha256Digest(value: unknown): value is `sha256:${string}` {
  return typeof value === 'string' && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isBoundedStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= GATE_LEDGER_MAX_ARRAY_ITEMS &&
    value.every(isBoundedString)
  );
}

function isBoundedStringMap(value: unknown): value is Record<string, string> {
  return parseBoundedMap(value, (item) => isBoundedString(item));
}

function isBoundedNullableStringMap(
  value: unknown,
): value is Record<string, string | null> {
  return parseBoundedMap(value, (item) => item === null || isBoundedString(item));
}

function parseBoundedMap(
  value: unknown,
  validateValue: (item: unknown) => boolean,
): boolean {
  if (!isRecord(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= GATE_LEDGER_MAX_MAP_FIELDS &&
    entries.every(
      ([key, item]) => isBoundedString(key) && validateValue(item),
    )
  );
}

function safeJson(value: unknown): string | null {
  try {
    return JSON.stringify(value) ?? null;
  } catch {
    return null;
  }
}

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes +=
      codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
  }
  return bytes;
}
